import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useCart } from '@/store/cartStore';

// Layouts
import MainLayout from '@/components/layout/MainLayout';

// Pages
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import CategoriesPage from '@/pages/categories/CategoriesPage';
import CategoryDetailPage from '@/pages/categories/CategoryDetailPage';
import SellerApplicationPage from '@/pages/seller/SellerApplicationPage';
import SellerDashboardPage from '@/pages/seller/SellerDashboardPage';
/*import ProductsPage from '@/pages/products/ProductsPage';
import ProductDetailPage from '@/pages/products/ProductDetailPage'; 
import OrdersPage from '@/pages/orders/OrdersPage';
import OrderDetailPage from '@/pages/orders/OrderDetailPage';*/
import SearchPage from "@/pages/SearchPage";
import WishlistPage from "@/pages/WishlistPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import ProfilePage from '@/pages/profile/ProfilePage';


import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminSellerRequestsPage from '@/pages/admin/AdminSellerRequestsPage';
import AdminRoute from '@/components/guards/AdminRoute';

// Error pages
//import NotFoundPage from '@/pages/errors/NotFoundPage';
import ErrorBoundary from '@/components/common/ErrorBoundary';

// Guards
import ProtectedRoute from '@/components/guards/ProtectedRoute';
import GuestRoute from '@/components/guards/GuestRoute';

// Global styles
import './App.css';
import AdminLayout from './components/layout/AdminLayout';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminReviewsMangamentPage from './pages/admin/AdminReviewsManagementPage';
import SellerProductFormPage from './pages/seller/SellerProductFormPage';
import SellerProductsPage from './pages/seller/SellerProductsPage';
import SellerProductViewPage from './pages/seller/SellerProductViewPage';
import SellerProductEditPage from './pages/seller/SellerProductEditPage';
import SellerOrdersPage from './pages/seller/SellerOrdersPage';
import SellerStorePage from './pages/seller/SellerStorePage';
import CartPage from './pages/CartPage';
import ProductsPage from './pages/products/ProductsPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import CheckoutPage from './pages/customer/CheckoutPage';
import OrderConfirmationPage from './pages/seller/OrderConfirmationPage';
import OrdersPage from './pages/customer/OrdersPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SellerProfilePage from './pages/seller/SellerProfilePage';
import SellerOrderDetailPage from './pages/seller/SellerOrderDetailPage';
import AdminOrderDetailPage from './components/admin/AdminOrderDetailPage';
import AdminSellerStorePage from './components/admin/AdminSellerStorePage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import NotFoundPage from './pages/NotFoundPage';

// Create a client para React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos (anteriormente cacheTime)
    },
    mutations: {
      retry: 1,
    },
  },
});

// Layout para páginas de autenticación (simplificado)
const AuthLayoutSimple: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        <Route path="login" element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        } />
        <Route path="register" element={
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        } />
      </Routes>
    </div>
  );
};

function App() {
  const { isAuthenticated, tokens } = useAuthStore();
  const { fetchCartCount } = useCart();

  // Inicializar datos cuando se monta la app
  useEffect(() => {
    // Si hay tokens válidos, cargar datos del usuario
    if (isAuthenticated && tokens?.access_token) {
      // Cargar contador del carrito
      fetchCartCount().catch(console.error);
    }
  }, [isAuthenticated, tokens, fetchCartCount]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}

        >
          <div className="App">
            <Routes>
              {/* ===== RUTAS DE ADMINISTRADOR ===== */}
              <Route path="admin" element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboardPage />} />
                <Route path="seller-requests" element={<AdminSellerRequestsPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="/admin/sellers/:sellerId" element={<AdminSellerStorePage />} />
                <Route path='categories' element={<AdminCategoriesPage />} />
                {/* Gestión de órdenes */}
                <Route path="orders" element={<AdminOrdersPage />} />
                {/*detalle de una orden*/}
                <Route path="orders/:id" element={<AdminOrderDetailPage />} />
                {/* Gestión de reseñas */}
                <Route path="reviews" element={<AdminReviewsMangamentPage />} />

                {/* Analíticas */}
                <Route path="analytics" element={<AdminAnalyticsPage />} />

                {/* Configuración */}
                <Route path="settings" element={
                  <div className="p-8 text-center">
                    <h2 className="text-xl font-bold mb-4">Configuración del Sistema</h2>
                    <p className="text-gray-600">Próximamente - Panel de configuración</p>
                  </div>
                } />

                {/* Log de actividad */}
                <Route path="activity-log" element={
                  <div className="p-8 text-center">
                    <h2 className="text-xl font-bold mb-4">Log de Actividad</h2>
                    <p className="text-gray-600">Próximamente - Registro de actividad del sistema</p>
                  </div>
                } />

                {/* Ruta por defecto del admin - redirigir al dashboard */}
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
              </Route>


              {/* Rutas de autenticación */}
              <Route path="/auth/*" element={<AuthLayoutSimple />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              {/* Rutas principales con layout */}
              <Route path="/" element={<MainLayout />}>
                {/* Página de inicio */}
                <Route index element={<HomePage />} />

                {/* Productos */}
                {/* <Route path="products" element={<ProductsPage />} />
                <Route path="product/:slug" element={<ProductDetailPage />} />
                <Route path="search" element={<SearchPage />} /> */}

                {/* Categorías */}
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="category/:id" element={<CategoryDetailPage />} />

                {/* Carrito (accesible para todos) */}
                <Route path="cart" element={<CartPage />} />

                <Route path="products" element={<ProductsPage />} />
                <Route path="product/:slug" element={<ProductDetailPage />} />


                <Route path="profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />
                {/* Rutas protegidas (requieren autenticación) */}
                {/* <Route path="checkout" element={
                  <ProtectedRoute>
                    <CheckoutPage />
                  </ProtectedRoute>
                } /> */}

                {/* <Route path="orders" element={
                  <ProtectedRoute>
                    <OrdersPage />
                  </ProtectedRoute>
                } />

                <Route path="orders/:id" element={
                  <ProtectedRoute>
                    <OrderDetailPage />
                  </ProtectedRoute>
                } />

                

                <Route path="wishlist" element={
                  <ProtectedRoute>
                    <WishlistPage />
                  </ProtectedRoute>
                } /> */}

                {/* Rutas de solo invitados */}
                <Route path="login" element={
                  <GuestRoute>
                    <LoginPage />
                  </GuestRoute>
                } />

                <Route path="register" element={
                  <GuestRoute>
                    <RegisterPage />
                  </GuestRoute>
                } />
                <Route path="forgot-password" element={
                  <GuestRoute>
                    <ForgotPasswordPage />
                  </GuestRoute>
                } />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/wishlist" element={<WishlistPage />} />
                <Route path="/help" element={<HelpCenterPage />} />
                <Route path="/track" element={<OrderTrackingPage />} />
                <Route path="seller/apply" element={
                  <ProtectedRoute requiredRole='customer'>
                    <SellerApplicationPage />
                  </ProtectedRoute>
                } />
                <Route path="/checkout" element={
                  <ProtectedRoute requiredRole='customer'>
                    <CheckoutPage />
                  </ProtectedRoute>
                } />
                <Route path="/order-confirmation/:orderId" element={
                  <ProtectedRoute requiredRole='customer'>
                    <OrderConfirmationPage />
                  </ProtectedRoute>
                } />
                <Route path="/orders" element={
                  <ProtectedRoute >
                    <OrdersPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/dashboard" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerDashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/products" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerProductsPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/products/new" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerProductFormPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/products/:productId" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerProductViewPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/products/edit/:productId" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerProductEditPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/orders" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerOrdersPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/orders/:id" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerOrderDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="seller/profile" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerProfilePage />
                    {/* <div className="p-8 text-center">Perfil de Tienda - Próximamente</div> */}
                  </ProtectedRoute>
                } />
                <Route path="seller/store/:sellerId" element={
                  <ProtectedRoute requiredRole='seller'>
                    <SellerStorePage />
                  </ProtectedRoute>
                } />

                {/* Redirecciones para compatibilidad */}
                <Route path="askSell" element={<Navigate to="/seller/apply" replace />} />

                {/* Redirecciones para compatibilidad */}
                <Route path="auth/login" element={<Navigate to="/login" replace />} />
                <Route path="auth/register" element={<Navigate to="/register" replace />} />
              </Route>

              {/* Página 404 */}
              <Route path="*" element={<NotFoundPage />} />
              {/* <Route path="*" element={<div>404 Not Found</div>} /> */}
            </Routes>
          </div>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;